import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
} from '@mui/material';
import { Add, ArrowForward } from '@mui/icons-material';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { WorkersV2API, ConsolidatedSkill } from '@/services/workersV2API';
import AddSkillModal from './AddSkillModal';

type Props = {
  workerId: number;
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'approved':
      return <Badge label="Approved" status="theme" />;
    case 'rejected':
      return <Badge label="Rejected" status="danger" />;
    case 'in_progress':
      return <Badge label="In Progress" status="warning" />;
    default:
      return <Badge label="Unknown" status="neutral" />;
  }
};

const getSkillRequestStatusBadge = (status: string | null) => {
  switch (status) {
    case 'accepted':
      return <Badge label="Accepted" status="theme" />;
    case 'rejected':
      return <Badge label="Rejected" status="danger" />;
    case 'pending':
      return <Badge label="Pending" status="warning" />;
    default:
      return <Badge label="-" status="neutral" />;
  }
};

const getAiAssessmentStatusBadge = (status: string | null) => {
  switch (status) {
    case 'passed':
      return <Badge label="Passed" status="theme" />;
    case 'failed':
      return <Badge label="Failed" status="danger" />;
    case 'for_review':
      return <Badge label="For Review" status="warning" />;
    case 'pending':
      return <Badge label="Pending" status="neutral" />;
    default:
      return <Badge label="-" status="neutral" />;
  }
};

const WorkerSkillsTable = ({ workerId }: Props) => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState<ConsolidatedSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await WorkersV2API.getConsolidatedSkills(workerId);
      if (response.success) {
        setSkills(response.data.skills);
      } else {
        setError('Failed to fetch worker skills');
      }
    } catch (err) {
      console.error('Error fetching consolidated skills:', err);
      setError('Failed to fetch worker skills');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkills();
  }, [workerId]);

  const handleViewSkill = (skillId: number) => {
    window.open(`/workers_v2/${workerId}/skill/${skillId}`, '_blank');
  };

  const handleAddSkill = () => {
    setShowAddModal(true);
  };

  const handleSkillAdded = (skillId: number) => {
    setShowAddModal(false);
    // Open the newly created skill page in a new tab
    window.open(`/workers_v2/${workerId}/skill/${skillId}`, '_blank');
  };

  if (loading) {
    return (
      <Card title="Worker Skills">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">Loading...</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title="Worker Skills">
        <Card.Section>
          <Stack style={{ justifyContent: 'center', padding: '20px' }}>
            <Small color="lighter">{error}</Small>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  return (
    <>
      <Card
        title={`Worker Skills (${skills.length})`}
        actionsDropdown={
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleAddSkill}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              minWidth: '140px',
              whiteSpace: 'nowrap',
            }}
          >
            Add Skill
          </Button>
        }
      >
        {skills.length === 0 ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '20px' }}>
              <Small color="lighter">No skills found</Small>
            </Stack>
          </Card.Section>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Skill Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Request Status</TableCell>
                  <TableCell>AI Assessment</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {skills.map((skill) => (
                  <TableRow
                    key={skill.skill_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewSkill(skill.skill_id)}
                  >
                    <TableCell>
                      <Small weight="semibold">{skill.skill_name}</Small>
                    </TableCell>
                    <TableCell>{getStatusBadge(skill.status)}</TableCell>
                    <TableCell>
                      {getSkillRequestStatusBadge(skill.skill_request_status)}
                    </TableCell>
                    <TableCell>
                      {getAiAssessmentStatusBadge(
                        skill.ai_assessment_final_status,
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewSkill(skill.skill_id);
                        }}
                      >
                        <ArrowForward fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <AddSkillModal
        open={showAddModal}
        workerId={workerId}
        onClose={() => setShowAddModal(false)}
        onSkillAdded={handleSkillAdded}
      />
    </>
  );
};

export default WorkerSkillsTable;
